import React, { ReactNode } from 'react';
import { SupportedSize } from '../Element/Element';
import Div from '../Element/Div/Div';
import Step from '../Step/Step';
import Steps from '../Steps/Steps';
import StepperItem from './StepperItem/StepperItem';

type StepperProps = {
  /** Index of the active step */
  selectedStepIndex: number;
  /** Function with arguments (index, selectedStepIndex) that determines whether a step is active */
  isStepActive?: (index: number, selectedStepIndex: number) => boolean;
  /** Function with arguments (index, selectedStepIndex) that determines whether a step is completed */
  isStepCompleted?: (index: number, selectedStepIndex: number) => boolean;
  /** Function with arguments (index, selectedStepIndex) that determines whether a step is selectable */
  isStepSelectable?: (index: number, selectedStepIndex: number) => boolean;
  /** Event handler with arguments (event, index), where index is the index of the selected step */
  onSelect?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => void;
  /** Any positive number of Stepper.Item elements */
  children: ReactNode[];
  /** Boolean that enables centering, passed to the Steps component */
  centerSteps?: boolean;
  /** Boolean that enables filling the component width, passed to the Steps component */
  spaceBetweenSteps?: boolean;
  /** An element (e.g. Icon) that appears inside a "completed" state indicator - true will use the default icon */
  completionIcon?: ReactNode;
  /** Boolean that triggers an always-bright variant for completed state items */
  alwaysBrightCompleted?: boolean;
  /** A size of a padding between steps */
  paddingSize?: SupportedSize;
};
// Alias for backwards compatibility
export type StepperAttributes = StepperProps;

const Stepper: React.FunctionComponent<StepperProps> & {
  Item: typeof StepperItem;
} = ({
  selectedStepIndex,
  isStepActive,
  isStepCompleted,
  isStepSelectable,
  onSelect,
  centerSteps,
  spaceBetweenSteps,
  completionIcon,
  alwaysBrightCompleted,
  children,
  paddingSize = 'md',
  ...props
}) => {
  const validItems = React.Children.toArray(children).filter(
    (candidate: any) => {
      return candidate.type === StepperItem;
    }
  );

  const steps = validItems.map((item: any, index: number) => {
    const label = item.props.label || String(index + 1);
    const {
      description,
      descriptionResponsiveHidden,
      stepContentToColumn,
    } = item.props;
    return (
      <Step
        label={label}
        description={description}
        descriptionResponsiveHidden={descriptionResponsiveHidden}
        stepContentToColumn={stepContentToColumn}
        key={String(index)}
      />
    );
  });

  const restrictedActiveIndex = Math.min(
    selectedStepIndex,
    validItems.length - 1
  );
  const activeComponent = validItems[restrictedActiveIndex];

  return (
    <Div>
      <Div padding={{ size: paddingSize }} {...props}>
        <Steps
          selectedStepIndex={restrictedActiveIndex}
          isStepActive={isStepActive}
          isStepCompleted={isStepCompleted}
          isStepSelectable={isStepSelectable}
          centerSteps={centerSteps}
          spaceBetweenSteps={spaceBetweenSteps}
          completionIcon={completionIcon}
          alwaysBrightCompleted={alwaysBrightCompleted}
          onSelect={onSelect}
        >
          {steps}
        </Steps>
      </Div>
      {activeComponent}
    </Div>
  );
};

Stepper.Item = StepperItem;

export default Stepper;
